import * as React from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '@/lib/store';
import * as LucideIcons from 'lucide-react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import { t } from 'i18next';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import {
  fetchMenu,
  fetchFavorites,
  selectFavorites,
  deleteFavorite,
  updateFavoriteOrder,
  reorderFavoritesLocally,
  type MenuItem,
} from '@/lib/menuSlice';
import { COLORS } from '@/utils/constants/colors';
import CustomButton from '@/components/CustomButton';
import SearchBar from './search-bar';
import { Separator } from '../ui/separator';

type LucideIconComponent = React.ComponentType<{ size?: number }>;

export const getIconComponent = (
  icon?: string
): LucideIconComponent | undefined => {
  if (!icon) return undefined;

  return LucideIcons[icon as keyof typeof LucideIcons] as
    | LucideIconComponent
    | undefined;
};

function DashboardSidebar() {
  const dispatch = useDispatch<AppDispatch>();
  const favoritesMenu = useSelector(selectFavorites);
  const menuItems = useSelector((state: RootState) => state.menu.items);
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === 'collapsed';

  useEffect(() => {
    dispatch(fetchMenu());
    dispatch(fetchFavorites());
  }, [dispatch]);

  const handleFavoriteReorder = (result: DropResult) => {
    if (!result.destination) return;

    const reordered = Array.from(favoritesMenu);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    const reorderedWithOrder = reordered.map((fav: any, idx: number) => ({
      ...fav,
      order: idx,
    }));
    const movedItem = reorderedWithOrder[result.destination.index];
    let pageRouteID = movedItem.pageRouteID;
    if (!pageRouteID) {
      const match = menuItems.find(
        (item: any) =>
          item.favouriteId === movedItem.favouriteId || item.id === movedItem.id
      );
      pageRouteID = match?.pageRouteID;
    }

    dispatch(reorderFavoritesLocally(reorderedWithOrder));
    dispatch(
      updateFavoriteOrder({
        favouriteId: movedItem.favouriteId,
        pageRouteID,
        newOrder: result.destination.index,
      })
    );
  };

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader
        className={cn(
          'flex pt-2',
          isCollapsed
            ? 'flex-row items-center justify-between gap-y-4 md:flex-col md:items-start md:justify-start'
            : 'flex-row items-center justify-between'
        )}
      >
        <motion.div
          key={isCollapsed ? 'header-collapsed' : 'header-expanded'}
          className={cn(
            'flex w-full items-center justify-between gap-2',
            isCollapsed ? 'flex-row md:flex-col -mt-2' : 'flex-row'
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <Link
            to={'/'}
            onClick={() => {
              if (isMobile) {
                setOpenMobile(false);
              }
            }}
            className='mb-1'
          >
            <img
              src="/logo.svg"
              alt="ApexScouty"
              className={cn('h-7 w-auto', isCollapsed && 'hidden')}
            />
          </Link>
          <SidebarTrigger />
        </motion.div>
      </SidebarHeader>
      {!isCollapsed && <Separator />}
      <SidebarContent className="flex flex-1 flex-col items-center gap-2">
        <div className={cn(!isCollapsed && 'w-full', 'pt-2')}>
          <SearchBar />
        </div>
        {favoritesMenu.length > 0 && (
          <SidebarGroup className="space-y-1">
            {state === 'expanded' && (
              <SidebarGroupLabel>{t('sidebar.favorites')}</SidebarGroupLabel>
            )}

            <DragDropContext onDragEnd={handleFavoriteReorder}>
              <Droppable droppableId="favorites">
                {provided => (
                  <SidebarMenu
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-0.5"
                  >
                    {favoritesMenu.map((item: MenuItem, idx: number) => {
                      const Icon = getIconComponent(item.icon);
                      const draggableId = String(item.favouriteId || item.id);
                      const colorClasses = COLORS[idx % COLORS.length];

                      return (
                        <Draggable
                          key={draggableId}
                          draggableId={draggableId}
                          index={idx}
                        >
                          {provided => (
                            <SidebarMenuItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={cn('group')}
                            >
                              <SidebarMenuButton
                                asChild
                                tooltip={item.name}
                                size="lg"
                                className={cn(
                                  'h-9 px-0.5',
                                  isCollapsed && '-ml-1'
                                )}
                              >
                                <Link
                                  to={item.path ?? '#'}
                                  onClick={() => {
                                    if (isMobile) {
                                      setOpenMobile(false);
                                    }
                                  }}
                                >
                                  {Icon && (
                                    <div
                                      className={cn(
                                        colorClasses,
                                        isCollapsed && 'p-2'
                                      )}
                                    >
                                      <Icon />
                                    </div>
                                  )}
                                  {!isCollapsed && (
                                    <span>{item.name || item.label}</span>
                                  )}
                                </Link>
                              </SidebarMenuButton>
                              <div className="flex items-center gap-0.5 opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100">
                                {item.favouriteId && (
                                  <SidebarMenuAction asChild>
                                    <CustomButton
                                      variant="ghost"
                                      size="icon-xs"
                                      className="hover:bg-muted translate-x-1 scale-0 transition-all duration-300 ease-in-out group-hover:translate-x-0 group-hover:scale-100"
                                      icon={
                                        <LucideIcons.Star
                                          fill="#facc15"
                                          stroke="#facc15"
                                          className="size-3.5!"
                                        />
                                      }
                                      onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        dispatch(
                                          deleteFavorite(item.favouriteId!)
                                        );
                                      }}
                                    />
                                  </SidebarMenuAction>
                                )}
                              </div>
                            </SidebarMenuItem>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </SidebarMenu>
                )}
              </Droppable>
            </DragDropContext>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}

export default DashboardSidebar;
